import React, { useEffect, useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import ViewDocumentButton from '~/components/Documents/ViewDocumentButton';
import { ExposureDetailedTitle } from '~/components/exposures/ExposureDetailedTitle';
import LoadingIndicator from '~/components/LoadingIndicator';
import PlainTable from '~/components/PlainTable';
import type { ExposureModel } from '~/components/types/exposure-types';
import { reportAxiosError } from '~/Utils';

import type { Invoice } from '../types/invoice-types';

interface CardDialogProps {
  open: boolean;
  onClose: () => void;
  [key: string]: unknown;
}

interface Props {
  exposure: ExposureModel;
  cardDialogProps: CardDialogProps;
}

interface TableRow {
  view_document: React.ReactNode;
  id: string | number;
  invoice_number: string;
  decision_date: string;
  submitted_amount: string;
  not_covered_amount: string;
  covered_amount: string;
  co_insurance: string;
  eligible_for_reimbursement: string;
}

interface CoverageTotals {
  submitted_amount: number;
  not_covered_amount: number;
  covered_amount: number;
  co_insurance: number;
  eligible_for_reimbursement: number;
}

interface InvoiceCoverageTotalsResponse {
  results: {
    invoice: Invoice;
    coverage_totals: CoverageTotals;
  }[];
  metadata: {
    with_coinsurance: boolean;
  };
}

const CURRENCY_COLUMNS = [
  'submitted_amount',
  'not_covered_amount',
  'covered_amount',
  'co_insurance',
  'eligible_for_reimbursement',
] as const;

const COLUMN_DATA = [
  { id: 'view_document', numeric: false, label: 'View Document' },
  { id: 'id', numeric: false, label: 'ID #' },
  { id: 'invoice_number', numeric: false, label: 'Invoice Number' },
  { id: 'decision_date', numeric: false, label: 'Decision Date' },
  { id: 'submitted_amount', numeric: true, label: 'Submitted' },
  { id: 'not_covered_amount', numeric: true, label: 'Not Covered' },
  { id: 'covered_amount', numeric: true, label: 'Covered' },
  { id: 'co_insurance', numeric: true, label: 'Co-Insurance' },
  { id: 'eligible_for_reimbursement', numeric: true, label: 'Eligible For Reimbursement' },
] as const;

const InvoiceDamageAssessmentCard: React.FC<Props> = ({ exposure, cardDialogProps }) => {
  const [invoiceCoverageTotals, setInvoiceCoverageTotals] = useState<InvoiceCoverageTotalsResponse | null>(null);
  const { currencyFormatter } = useCurrencyFormatter();
  const { claim } = useClaim({ claimRequired: true });
  const coverageId = exposure.coverage?.id ?? 0;

  const columnData = React.useMemo(() => {
    if (invoiceCoverageTotals) {
      if (!invoiceCoverageTotals.metadata.with_coinsurance) {
        // Hide co_insurance and eligible_for_reimbursement columns
        return COLUMN_DATA.map((col) =>
          col.id === 'co_insurance' || col.id === 'eligible_for_reimbursement'
            ? { ...col, isHidden: true }
            : { ...col, isHidden: false }
        );
      }
    }
    // Default: all columns visible
    return COLUMN_DATA;
  }, [invoiceCoverageTotals]);

  useEffect(() => {
    const fetchInvoiceCoverageTotals = async () => {
      try {
        const response = await axios.get<InvoiceCoverageTotalsResponse>(
          `/api/v1/claims/${claim.id}/invoice_based_da/coverage_totals/${coverageId}`
        );
        setInvoiceCoverageTotals(response.data);
      } catch (error) {
        await reportAxiosError(error);
      }
    };
    fetchInvoiceCoverageTotals();
  }, [claim, coverageId]);

  const createInvoiceRow = (invoice: Invoice, coverageTotals: CoverageTotals): TableRow => {
    const document = (claim?.documents ?? []).find((doc) => doc.id === invoice.document_id);
    return {
      view_document: document ? <ViewDocumentButton document={document} /> : null,
      id: document?.claim_internal_id || '',
      invoice_number: invoice.invoice_number,
      decision_date: new Date(invoice.updated_at).toLocaleDateString(),
      ...CURRENCY_COLUMNS.reduce(
        (acc, column) => ({
          ...acc,
          [column]: currencyFormatter.format(coverageTotals[column]),
        }),
        {} as Record<(typeof CURRENCY_COLUMNS)[number], string>
      ),
    };
  };

  const calculateTotals = (): Record<(typeof CURRENCY_COLUMNS)[number], number> => {
    return invoiceCoverageTotals
      ? invoiceCoverageTotals.results.reduce(
          (acc, item) => {
            for (const column of CURRENCY_COLUMNS) {
              acc[column] = (acc[column] || 0) + (item.coverage_totals[column] || 0);
            }
            return acc;
          },
          {} as Record<(typeof CURRENCY_COLUMNS)[number], number>
        )
      : ({} as Record<(typeof CURRENCY_COLUMNS)[number], number>);
  };

  const rows = invoiceCoverageTotals
    ? invoiceCoverageTotals.results.map((item) => createInvoiceRow(item.invoice, item.coverage_totals))
    : [];
  const totals = calculateTotals();

  const totalRow: TableRow = {
    view_document: <strong>Total</strong>,
    id: '',
    invoice_number: '',
    decision_date: '',
    ...CURRENCY_COLUMNS.reduce(
      (acc, column) => ({
        ...acc,
        [column]: currencyFormatter.format(totals[column]),
      }),
      {} as Record<(typeof CURRENCY_COLUMNS)[number], string>
    ),
  };

  const allRows = [...rows, totalRow];

  return (
    <CardDialog
      title={
        <div className="flex items-center">
          <Typography variant="h6" style={{ marginRight: '4px' }}>
            Damage Assessment -
          </Typography>
          <ExposureDetailedTitle exposure={exposure} />
        </div>
      }
      maxWidth="md"
      {...cardDialogProps}
    >
      {invoiceCoverageTotals ? (
        <Grid container spacing={2}>
          {invoiceCoverageTotals.results.length > 0 && (
            <Grid item xs={12} style={{ marginBottom: 16 }}>
              <PlainTable columns={columnData} rows={allRows} keepRowsOrder />
            </Grid>
          )}
          <Grid item xs={6}>
            <Typography variant="body2">Total invoices: {invoiceCoverageTotals.results.length}</Typography>
          </Grid>
        </Grid>
      ) : (
        <LoadingIndicator />
      )}
    </CardDialog>
  );
};

export default InvoiceDamageAssessmentCard;
